/* Luxe & Gloss collection discovery and card purchase entry — version 1. */

(() => {
  let activeCollectionRequest
  let quickShopContextSequence = 0

  const collectionRoot = () => document.querySelector('[data-collection-root]')

  const initializeCardContexts = (scope = document, routeKey = `${window.location.pathname}${window.location.search}`) => {
    const lists = Array.from(scope.querySelectorAll('ul')).filter(list => list.querySelector('[data-lg-product-card]'))
    lists.forEach((list, index) => {
      if (!list.dataset.lgQuickshopContext) {
        quickShopContextSequence += 1
        list.dataset.lgQuickshopSequence = String(quickShopContextSequence)
        list.dataset.lgQuickshopContext = `${routeKey}::${list.id || index}::${quickShopContextSequence}`
      }
      list.querySelectorAll('[data-lg-product-card]').forEach((card, cardIndex) => {
        const trigger = card.querySelector('[data-lg-quickshop-trigger]')
        if (trigger && !trigger.id) trigger.id = `lg-quickshop-opener-${list.dataset.lgQuickshopSequence || 'list'}-${cardIndex}`
      })
      list.querySelectorAll('lg-card-gallery').forEach(gallery => {
        gallery.dataset.contextId = list.dataset.lgQuickshopContext
      })
    })
  }

  const quickShopDetail = trigger => {
    const card = trigger.closest('[data-lg-product-card]')
    const list = card?.closest('ul')
    if (!card || !list) return null

    initializeCardContexts(list.parentElement || document)
    const cards = Array.from(list.querySelectorAll('[data-lg-product-card]'))
      .filter(item => item.dataset.productUrl && item.dataset.productHandle)
    const index = cards.indexOf(card)
    if (index < 0) return null

    if (!trigger.id) {
      quickShopContextSequence += 1
      trigger.id = `lg-quickshop-opener-${quickShopContextSequence}`
    }

    const gallery = card.querySelector('lg-card-gallery')
    const visibleImage = gallery?.querySelector('.lg-card-gallery__image')
    const initialMediaId = visibleImage?.dataset.mediaId ||
      gallery?.querySelector('[data-lg-card-current-media-id]')?.textContent?.trim() ||
      gallery?.dataset.primaryMediaId || ''

    return {
      version: 1,
      contextId: list.dataset.lgQuickshopContext,
      sourceRoute: `${window.location.pathname}${window.location.search}`,
      products: cards.map(item => ({
        id: item.dataset.productId || '',
        handle: item.dataset.productHandle,
        url: item.dataset.productUrl
      })),
      index,
      productUrl: card.dataset.productUrl,
      productHandle: card.dataset.productHandle,
      initialMediaId,
      openerId: trigger.id,
      scrollX: window.scrollX,
      scrollY: window.scrollY
    }
  }

  const urlFromForm = form => {
    const url = new URL(form.action || window.location.href, window.location.origin)
    const params = new URLSearchParams(new FormData(form))

    for (const [key, value] of [...params.entries()]) {
      if (value === '') params.delete(key)
    }

    params.delete('page')
    url.search = params.toString()
    return url
  }

  const focusAfterUpdate = id => {
    if (!id) return
    window.requestAnimationFrame(() => document.getElementById(id)?.focus())
  }

  const announceResults = root => {
    const status = root?.querySelector('[data-collection-status]')
    if (status) status.textContent = root.dataset.resultsLabel || ''
  }

  const showCollectionError = root => {
    const error = root?.querySelector('[data-collection-error]')
    if (error) error.hidden = false
    document.querySelectorAll('[data-collection-filter-error]').forEach(filterError => {
      filterError.hidden = false
    })
  }

  const replaceFilterDrawer = newDocument => {
    const currentDrawer = document.querySelector('[data-collection-filter-drawer]')
    const nextDrawer = newDocument.querySelector('[data-collection-filter-drawer]')
    const currentFilters = currentDrawer?.querySelector('lg-collection-filters')
    const nextFilters = nextDrawer?.querySelector('lg-collection-filters')

    if (currentFilters && nextFilters) currentFilters.replaceWith(nextFilters)
  }

  const loadCollection = async (nextUrl, options = {}) => {
    const currentRoot = collectionRoot()
    if (!currentRoot) return false

    const requestUrl = new URL(nextUrl, window.location.origin)
    if (requestUrl.origin !== window.location.origin) return false

    activeCollectionRequest?.abort()
    const controller = new AbortController()
    activeCollectionRequest = controller

    currentRoot.setAttribute('aria-busy', 'true')
    currentRoot.classList.add('is-loading')
    const currentError = currentRoot.querySelector('[data-collection-error]')
    if (currentError) currentError.hidden = true
    document.querySelectorAll('[data-collection-filter-error]').forEach(filterError => {
      filterError.hidden = true
    })

    try {
      const response = await fetch(requestUrl.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`Collection request failed: ${response.status}`)

      const html = await response.text()
      const newDocument = new DOMParser().parseFromString(html, 'text/html')
      const nextRoot = newDocument.querySelector('[data-collection-root]')
      if (!nextRoot) throw new Error('Collection response did not include results')

      initializeCardContexts(nextRoot, `${requestUrl.pathname}${requestUrl.search}`)

      currentRoot.replaceWith(nextRoot)
      replaceFilterDrawer(newDocument)

      if (options.history === 'push') {
        window.history.pushState({ lgCollection: true }, '', requestUrl.href)
      } else if (options.history === 'replace') {
        window.history.replaceState({ lgCollection: true }, '', requestUrl.href)
      }

      announceResults(nextRoot)
      focusAfterUpdate(options.focusId)

      if (options.scroll) {
        nextRoot.scrollIntoView({ block: 'start', behavior: 'auto' })
      }

      window.dispatchEvent(new CustomEvent('kt.collection.loaded'))
      return true
    } catch (error) {
      if (error.name !== 'AbortError') showCollectionError(collectionRoot() || currentRoot)
      return false
    } finally {
      if (activeCollectionRequest === controller) {
        const root = collectionRoot()
        root?.removeAttribute('aria-busy')
        root?.classList.remove('is-loading')
        activeCollectionRequest = undefined
      }
    }
  }

  class LGCollectionFilters extends HTMLElement {
    connectedCallback () {
      if (this.dataset.initialized === 'true') return
      this.dataset.initialized = 'true'
      this.form = this.querySelector('[data-collection-filter-form]')
      if (!this.form) return

      this.form.addEventListener('change', event => {
        if (!event.target.name || event.target.type === 'hidden') return
        this.update(event.target.id)
      })

      this.form.addEventListener('submit', event => {
        event.preventDefault()
        const nextUrl = urlFromForm(this.form)
        if (`${nextUrl.pathname}${nextUrl.search}` !== `${window.location.pathname}${window.location.search}`) {
          this.update()
        }
      })
    }

    update (focusId) {
      loadCollection(urlFromForm(this.form), {
        history: 'push',
        focusId,
        scroll: false
      })
    }
  }

  class LGCollectionSort extends HTMLElement {
    connectedCallback () {
      if (this.dataset.initialized === 'true') return
      this.dataset.initialized = 'true'
      this.form = this.querySelector('[data-collection-sort-form]')
      this.select = this.querySelector('[data-collection-sort]')
      if (!this.form || !this.select) return

      this.select.addEventListener('change', () => {
        loadCollection(urlFromForm(this.form), {
          history: 'push',
          focusId: this.select.id,
          scroll: false
        })
      })
    }
  }

  class LGCardForm extends HTMLElement {
    connectedCallback () {
      if (this.dataset.initialized === 'true') return
      this.dataset.initialized = 'true'
      this.form = this.querySelector('form')
      this.variantSelect = this.querySelector('[data-lg-card-variant]')
      this.submitButton = this.querySelector('[data-lg-card-submit]')
      this.status = this.querySelector('[role="status"]')
      this.error = this.querySelector('[role="alert"]')
      if (!this.form || !this.submitButton) return

      if (this.variantSelect) {
        this.submitButton.disabled = !this.variantSelect.value
        this.variantSelect.addEventListener('change', () => {
          this.submitButton.disabled = !this.variantSelect.value
          this.hideMessages()
        })
      }

      this.form.addEventListener('submit', event => this.submit(event))
    }

    hideMessages () {
      if (this.status) this.status.hidden = true
      if (this.error) this.error.hidden = true
    }

    setBusy (busy) {
      this.form.toggleAttribute('aria-busy', busy)
      this.submitButton.classList.toggle('loading', busy)
      this.submitButton.disabled = busy || Boolean(this.variantSelect && !this.variantSelect.value)
    }

    async submit (event) {
      const cart = window.LG?.cart

      // Preserve Shopify's native form post until G's C4 service is available.
      if (!cart || typeof cart.add !== 'function') return
      if (!this.form.reportValidity()) return

      event.preventDefault()
      this.hideMessages()

      const formData = new FormData(this.form)
      const id = Number(formData.get('id'))
      const quantity = Math.max(1, Number(formData.get('quantity')) || 1)
      if (!Number.isSafeInteger(id) || id <= 0) {
        this.error.hidden = false
        return
      }

      this.setBusy(true)
      let addConfirmed = false
      try {
        await cart.add({
          items: [{ id, quantity }],
          source: this.dataset.source || 'product-card'
        })
        addConfirmed = true
      } catch (error) {
        const errorMessage = this.error?.querySelector('[data-lg-card-error-message]')
        if (errorMessage && error?.message) errorMessage.textContent = error.message
        if (this.error) this.error.hidden = false
      } finally {
        this.setBusy(false)
      }

      if (!addConfirmed) return

      if (this.status) {
        this.status.textContent = 'Added to your bag.'
        this.status.hidden = false
      }

    }
  }

  if (!customElements.get('lg-collection-filters')) {
    customElements.define('lg-collection-filters', LGCollectionFilters)
  }
  if (!customElements.get('lg-collection-sort')) {
    customElements.define('lg-collection-sort', LGCollectionSort)
  }
  if (!customElements.get('lg-card-form')) {
    customElements.define('lg-card-form', LGCardForm)
  }

  document.addEventListener('click', event => {
    const quickShopTrigger = event.target.closest('[data-lg-quickshop-trigger]')
    if (quickShopTrigger && !quickShopTrigger.disabled) {
      const detail = quickShopDetail(quickShopTrigger)
      if (detail) {
        event.preventDefault()
        window.dispatchEvent(new CustomEvent('lg:quickshop:open', { detail }))
      }
      return
    }

    const link = event.target.closest('[data-collection-link]')
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const nextUrl = new URL(link.href, window.location.origin)
    if (nextUrl.origin !== window.location.origin || !collectionRoot()) return

    event.preventDefault()
    loadCollection(nextUrl, {
      history: 'push',
      focusId: link.closest('.lg-filter-drawer') ? link.id : undefined,
      scroll: Boolean(link.closest('.lg-pagination'))
    })
  })

  if (!window.__lgCollectionPopstateBound) {
    window.__lgCollectionPopstateBound = true
    window.addEventListener('popstate', () => {
      if (document.querySelector('lg-nail-finder')) return
      if (window.LG?.quickShop?.isOpen?.() || new URL(window.location.href).searchParams.has('view_product')) return
      if (collectionRoot()) loadCollection(window.location.href, { scroll: false })
    })
  }

  initializeCardContexts()
  window.addEventListener('lg:finder:results', event => {
    initializeCardContexts(event.detail?.root || document, `${window.location.pathname}${window.location.search}`)
  })
  document.addEventListener('shopify:section:load', event => initializeCardContexts(event.target))
})()
